/**
 * HIPAA Notice Page
 * ==================
 * HIPAA Notice of Privacy Practices.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, FileLock } from 'lucide-react';
import { Card } from '@/components/ui/Card';

export default function HipaaNotice() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/register"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Registration
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
            <FileLock className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">HIPAA Notice of Privacy Practices</h1>
          <p className="mt-2 text-gray-600">Last updated: March 12, 2026</p>
        </div>

        {/* Content */}
        <Card className="p-8 prose prose-sm max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Notice of Privacy Practices</h2>
            <p className="text-gray-700 leading-relaxed">
              This notice describes how medical information about you may be used and disclosed and how you can get access 
              to this information. Please review it carefully.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
              Effective Date: March 12, 2026
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Legal Duty</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are required by applicable federal and state law to maintain the privacy of your Protected Health Information (PHI). 
              We are also required to give you this Notice about our privacy practices, our legal duties, and your rights concerning 
              your PHI. We must follow the privacy practices that are described in this Notice while it is in effect.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Notice takes effect on March 12, 2026, and will remain in effect until we replace it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We May Use and Disclose Health Information</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">1. Treatment</h3>
            <p className="text-gray-700 leading-relaxed">
              We may use your PHI to provide, coordinate, or manage your healthcare and related services. This includes 
              communication with other healthcare providers regarding your treatment and care coordination.
            </p>
            <p className="text-gray-700 leading-relaxed mt-2 italic">
              Example: Your periodontal measurements and treatment plans may be shared with your general dentist or 
              other specialists involved in your care.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">2. Payment</h3>
            <p className="text-gray-700 leading-relaxed">
              We may use and disclose your PHI to obtain payment for services we provide to you. This may include 
              billing, claims management, and determining eligibility for insurance benefits.
            </p>
            <p className="text-gray-700 leading-relaxed mt-2 italic">
              Example: We may submit claims to your dental insurance company with information about your periodontal 
              treatment to receive payment.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">3. Healthcare Operations</h3>
            <p className="text-gray-700 leading-relaxed">
              We may use and disclose your PHI for healthcare operations purposes. These activities are necessary to 
              run our practice and ensure quality care.
            </p>
            <p className="text-gray-700 leading-relaxed mt-2 italic">
              Example: We may use your health information to evaluate the quality of treatment and services, for 
              business planning, and for training purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Other Uses and Disclosures</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Business Associates</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your PHI to our business associates who perform services on our behalf. All business 
              associates are obligated to protect the privacy of your information and are not allowed to use or disclose 
              any information other than as specified in our contract with them.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Required by Law</h3>
            <p className="text-gray-700 leading-relaxed">
              We will disclose your PHI when required to do so by federal, state, or local law.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Public Health Activities</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your PHI for public health activities, such as reporting diseases, injuries, vital events, 
              and conducting public health surveillance.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Abuse or Neglect</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your PHI to appropriate authorities if we reasonably believe that you are a victim of abuse, 
              neglect, or domestic violence.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Law Enforcement</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your PHI to law enforcement officials for purposes such as identifying or locating a suspect 
              or complying with a court order or subpoena.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights Regarding Your Health Information</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to Inspect and Copy</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to inspect and obtain a copy of your PHI contained in your medical and billing records. 
              To request copies, you must submit a written request to our Privacy Officer.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to Amend</h3>
            <p className="text-gray-700 leading-relaxed">
              If you believe that information in your record is incorrect or incomplete, you may request that we amend the 
              information. We may deny your request under certain circumstances.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to an Accounting of Disclosures</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to receive a list of certain disclosures we have made of your PHI. This list will not 
              include disclosures made for treatment, payment, or healthcare operations.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to Request Restrictions</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to request restrictions on certain uses and disclosures of your PHI. We are not required 
              to agree to your request unless you are asking us to restrict disclosure to a health plan for payment or 
              healthcare operations purposes and the information pertains solely to services for which you paid out of pocket.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to Request Confidential Communications</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to request that we communicate with you about medical matters in a certain way or at a 
              certain location. We will accommodate reasonable requests.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to a Paper Copy of This Notice</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to receive a paper copy of this Notice upon request, even if you have agreed to receive 
              it electronically.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Right to be Notified of a Breach</h3>
            <p className="text-gray-700 leading-relaxed">
              You have the right to be notified in the event that we (or one of our business associates) discover a breach 
              of your unsecured PHI.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Notice</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to change this Notice. We reserve the right to make the revised or changed Notice 
              effective for medical information we already have about you as well as any information we receive in the 
              future. The current Notice will always be posted in our office and on our website with the effective date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complaints</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you believe your privacy rights have been violated, you may file a complaint with us or with the Secretary 
              of the Department of Health and Human Services. You will not be penalized for filing a complaint.
            </p>
            <p className="text-gray-700 leading-relaxed">
              To file a complaint with us, contact our Privacy Officer using the information below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For more information about our privacy practices, or to file a complaint, contact:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Privacy Officer</strong></p>
              <p className="text-gray-700">Dentulu Periodontal Charting System</p>
              <p className="text-gray-700">Email: privacy@dentulu.com</p>
              <p className="text-gray-700">Phone: 1-800-DENTULU</p>
              <p className="text-gray-700 mt-2">
                Mailing Address:<br />
                Privacy Officer<br />
                Dentulu HIPAA Compliance<br />
                123 Healthcare Drive<br />
                San Francisco, CA 94102
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              To file a complaint with the Department of Health and Human Services:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">Office for Civil Rights</p>
              <p className="text-gray-700">U.S. Department of Health and Human Services</p>
              <p className="text-gray-700">200 Independence Avenue, S.W.</p>
              <p className="text-gray-700">Washington, D.C. 20201</p>
              <p className="text-gray-700">Phone: 1-877-696-6775</p>
              <p className="text-gray-700">Website: www.hhs.gov/ocr/privacy/hipaa/complaints/</p>
            </div>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              This Notice of Privacy Practices is effective as of March 12, 2026.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
